import React from 'react';
import CodeBlock from '../components/CodeBlock';

const SendTemplate = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>📤 Envoyer un message template</h1>
        <p>
          Envoyez un message WhatsApp en utilisant un template approuvé par Meta.
          Requiert un JWT (Bearer) et le header <code>X-Workspace-Id</code>.
        </p>
      </div>

      <div className="section">
        <h2>POST /api/v1/messages/send-template</h2>
        <p>
          Envoie un message template à un numéro de téléphone. Le template doit exister dans le workspace
          et être au statut <strong>APPROVED</strong> côté Meta.
        </p>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/v1/messages/send-template</span>
          </div>
          <div className="endpoint-description">
            Envoi d'un message template WhatsApp (variables positionnelles)
          </div>

          <div className="alert alert-info" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            ⚠️ Nécessite authentification JWT + header <code>X-Workspace-Id</code> + scope <code>messages:send</code>
          </div>

          <h4>Headers requis :</h4>
          <table className="params-table">
            <thead>
              <tr>
                <th>Header</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Authorization</code></td>
                <td>Bearer JWT_TOKEN (obtenu via POST /api/public/auth avec X-API-Key)</td>
              </tr>
              <tr>
                <td><code>X-Workspace-Id</code></td>
                <td>ID du workspace dans lequel se trouve le template</td>
              </tr>
              <tr>
                <td><code>Content-Type</code></td>
                <td>application/json</td>
              </tr>
            </tbody>
          </table>

          <h4>Body (JSON) :</h4>
          <table className="params-table">
            <thead>
              <tr>
                <th>Champ</th>
                <th>Type</th>
                <th>Obligatoire</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>to</code></td>
                <td>string</td>
                <td>Oui</td>
                <td>Numéro du destinataire (E.164, ex. +33612345678).</td>
              </tr>
              <tr>
                <td><code>templateName</code></td>
                <td>string</td>
                <td>Oui</td>
                <td>Nom du template tel qu'enregistré dans le workspace</td>
              </tr>
              <tr>
                <td><code>language</code></td>
                <td>string</td>
                <td>Non</td>
                <td>Code langue du template (défaut : langue du template)</td>
              </tr>
              <tr>
                <td><code>variables</code></td>
                <td>array</td>
                <td>Non</td>
                <td>
                  Tableau <strong>positionnel</strong> des valeurs des variables du body, dans l'ordre :
                  le 1<sup>er</sup> élément remplit <code>{'{{1}}'}</code>, le 2<sup>e</sup> remplit <code>{'{{2}}'}</code>, etc.
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Exemple de requête :</h4>
          <CodeBlock
            code={`curl -X POST https://api.whakup.com/api/v1/messages/send-template \\
  -H "Authorization: Bearer VOTRE_JWT_TOKEN" \\
  -H "X-Workspace-Id: 456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+33612345678",
    "templateName": "welcome_message",
    "language": "fr",
    "variables": ["Jean", "Dupont"]
  }'`}
            language="curl"
            showCopy={true}
          />

          <h4>Réponse succès (200) :</h4>
          <CodeBlock
            code={`{
  "messageId": "wamid.xxxxxx",
  "to": "+33612345678",
  "templateName": "welcome_message",
  "status": "sent"
}`}
            language="json"
            showCopy={true}
          />

          <h4>Erreurs possibles :</h4>
          <ul>
            <li><strong>400</strong> — <code>to</code> ou <code>templateName</code> manquant</li>
            <li><strong>404</strong> — Template introuvable pour ce nom dans le workspace</li>
            <li><strong>400</strong> — Erreur renvoyée par Meta (numéro invalide, template refusé, etc.)</li>
            <li><strong>500</strong> — Erreur serveur lors de l'envoi</li>
          </ul>
        </div>
      </div>

      <div className="section">
        <h2>📝 Variables du template</h2>
        <p>
          Les variables sont fournies sous forme de <strong>tableau positionnel</strong> : l'ordre des éléments
          correspond à l'ordre des placeholders <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, … du body du template.
        </p>
      </div>

      <div className="section">
        <h2>🕘 Endpoint legacy</h2>
        <p>
          Un ancien endpoint <code>POST /api/public/messages/send-template</code> reste disponible pour
          rétro-compatibilité. Il attend les variables sous forme d'<strong>objet</strong> (clés par index
          <code>"0"</code>, <code>"1"</code>, … ou par nom de placeholder ; <code>button_0</code> pour les boutons URL
          dynamiques) et renvoie une réponse au format <code>{`{ messageId, status, template, phone, metaStatus, sentAt }`}</code>.
        </p>
        <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
          💡 Pour toute nouvelle intégration, utilisez <code>/api/v1/messages/send-template</code> ci-dessus.
        </div>
      </div>
    </div>
  );
};

export default SendTemplate;
